import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Loader,
  buildNotification,
  GenerateNotification,
  isFromValid,
  isValidate,
} from "smart-react";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Trash2,
  ChevronDown,
  Save,
  MoreVertical,
} from "lucide-react";

import { useAuth } from "../../../Core/Context/AuthContext";
import TenantDropdown from "../../../Core/Components/TenantDropdown/TenantDropdown";
import { ListView } from "../../../Core/Components/ListView/ListView";
import {
  listRFProfile,
  deleteRFProfile,
} from "../../RFProfile/Services/RFProfileService";
import { getTokensFromStorage } from "../../../../Utils/Auth/AuthToken";
import {
  objectToBase64,
  base64ToObject,
} from "../../../../Utils/Common/commonUtils";

import {
  createRFProfileGroup,
  updateRFProfileGroup,
  getRFProfileGroup,
  deleteRFProfileGroup,
} from "../Services/RFProfileGroupService";
import {
  ValidatePermissions,
  findTenant,
  findTenantById,
} from "../../../../Utils/Auth/ValidatePermissions";

import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from "../../../../constants/eventDataTypes";
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
} from "../../../../constants/notificationMessages";

type RFProfileGroupForm = {
  TenantID: string | number;
  TenantName?: string;
  SRFProfileGroupId?: string | number;
  SRFProfileGroupName: string;
  SRFProfileGroupDetails?: string;
};

type RFProfileItem = {
  SRFProfileId: string | number;
  SRFProfileGroupId?: string | number;
  SRFProfilesName: string;
  SRFProfilesDetails?: string;
  AppURL?: string;
  ProfileDspSequence?: number;
  TenantId?: string | number;
  [k: string]: any;
};

function hasValue(v: any) {
  return v !== null && v !== undefined && String(v).trim() !== "";
}

function formatDateShort(d: string | Date) {
  try {
    const date = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(date.getTime())) return String(d);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
}

// Debounce state for profiles search (Profiles list under Profile Group edit)
let profilesSearchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastProfilesSearchTerm = "";

const AddRFProfileGroup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: { edit?: boolean; dataSet?: any };
    search: string;
  };
  const { state } = location;
  const { tenantId: tenantIdParam } = useParams();
  const refId = React.useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      return params.get("refId");
    } catch {
      return null;
    }
  }, [location.search]);
  const { tenantID } = useAuth();

  // UI/flow state
  const [loading, setLoading] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const [validateForm, setValidateForm] = React.useState(false);
  const [profilesRefreshKey, setProfilesRefreshKey] = React.useState(0);
  const [profileConfirmOpen, setProfileConfirmOpen] = React.useState(false);
  const profileDeleteConfirmRef = React.useRef<(() => void) | null>(null);
  const [expandedDesc, setExpandedDesc] = React.useState<
    Record<string | number, boolean>
  >({});

  // Tenant + form state
  const [tenant, setTenant] = React.useState<any>(findTenantById(tenantID));
  const [formData, setFormData] = React.useState<RFProfileGroupForm>({
    TenantID: tenantID,
    TenantName: tenant?.TenantName,
    SRFProfileGroupId: "",
    SRFProfileGroupName: "",
    SRFProfileGroupDetails: "",
  });
  const [tempState, setTempState] = React.useState<any>(state);

  // Derived
  const requiredFields = ["SRFProfileGroupName", "TenantName"] as const;

  // Load detail when editing
  React.useEffect(() => {
    const loadByState = async () => {
      try {
        setLoading(true);
        const response: any = await getRFProfileGroup({
          data: state!.dataSet,
          moduleName: "RFProfileGroup",
          isServiceWorker: false,
          listController: undefined,
        });
        const rfProfileData = response.Payload ?? state!.dataSet;
        setTempState({ ...state, dataSet: rfProfileData });
        setFormData((prev) => ({ ...prev, ...rfProfileData }));
        setTenant(findTenant(rfProfileData));
        if (ValidatePermissions(rfProfileData)) {
          setDetailsDataModified(true);
        }
      } finally {
        setLoading(false);
      }
    };

    const loadByRefId = async (rid: string) => {
      try {
        setLoading(true);
        const decoded = base64ToObject(rid as string);
        const response: any = await getRFProfileGroup({
          data: decoded,
          moduleName: "RFProfileGroup",
          isServiceWorker: false,
          listController: undefined,
        });
        const rfProfileData = response?.Payload ?? decoded;
        setTempState({ edit: true, dataSet: rfProfileData });
        setFormData((prev) => ({ ...prev, ...rfProfileData }));
        setTenant(findTenant(rfProfileData));
        if (ValidatePermissions(rfProfileData)) {
          setDetailsDataModified(true);
        }
      } finally {
        setLoading(false);
      }
    };

    if (state?.edit && state?.dataSet) {
      loadByState();
    } else if (refId) {
      loadByRefId(refId);
    } else {
      setDetailsDataModified(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.edit, refId]);

  // Keep tenant in sync when not editing
  React.useEffect(() => {
    if (!state?.edit) {
      const t: any = (findTenantById as any)(tenantID);
      setTenant(t);
      setFormData((prev) => ({
        ...prev,
        TenantID: tenantID,
        TenantName: t?.TenantName,
      }));
    }
  }, [tenantID, state?.edit]);

  // Current group id (fallbacks to ensure availability after details load)
  const currentGroupId =
    (formData as any)?.SRFProfileGroupId ??
    (tempState?.dataSet as any)?.SRFProfileGroupId ??
    (state?.dataSet as any)?.SRFProfileGroupId;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    const nextValue =
      type === "checkbox"
        ? (e.target as HTMLInputElement).checked
          ? 1
          : 0
        : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue as any }));
  };

  const onExit = () => navigate("/smartrf/profile-groups");

  const onSave = async ({
    isExit = false,
    isContinue = false,
  }: {
    isExit?: boolean;
    isContinue?: boolean;
  }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }

    setLoading(true);
    let notificationMetaData: any = {};
    const data = { ...formData };
    let response: any;
    let method: typeof createRFProfileGroup | typeof updateRFProfileGroup;

    if (state?.edit) {
      method = updateRFProfileGroup;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createRFProfileGroup;
      notificationMetaData = CREATE_MESSAGE;
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        moduleName: "RFProfileGroup",
        isServiceWorker: false,
        listController: undefined,
      });

      if (!response?.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response?.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );

        // Clear form if it was a create and staying on new
        if (!state?.edit && !isExit && !isContinue) {
          setFormData({
            TenantID: tenantID,
            TenantName: tenant?.TenantName,
            SRFProfileGroupId: "",
            SRFProfileGroupName: "",
            SRFProfileGroupDetails: "",
          });
        }

        // Update route state reference
        setTempState({
          ...state,
          edit: true,
          dataSet: response?.Payload,
        });

        if (isContinue) {
          const payload = response?.Payload;
          const refId = objectToBase64({
            SRFProfileGroupId: payload?.SRFProfileGroupId,
            SRFProfileGroupName: payload?.SRFProfileGroupName,
            TenantId: payload?.TenantId,
            TenantName: payload?.TenantName,
          });
          navigate(
            `/smartrf/profile-group/${payload?.TenantId}?refId=${refId}`,
            {
              state: { edit: true, dataSet: payload },
            },
          );
        }
        if (isExit) {
          navigate("/smartrf/profile-groups");
        }
      }
    } catch (err) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  // Split save button with dropdown ("Save" default, "Save & Close" in menu)
  const SplitSaveButton: React.FC<{
    onSave: (opts: { isExit?: boolean; isContinue?: boolean }) => void;
    disabled?: boolean;
  }> = ({ onSave, disabled }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
      if (!open) return;
      const onDoc = (e: MouseEvent) => {
        const t = e.target as Node;
        if (ref.current && !ref.current.contains(t)) setOpen(false);
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onDoc);
        document.removeEventListener("keydown", onKey);
      };
    }, [open]);

    return (
      <div ref={ref} className="relative inline-flex items-center">
        {/* Dashboard-like split button styling using theme tokens (works in light/dark) */}
        <div className="inline-flex overflow-hidden rounded-md border border-border bg-primary text-primary-foreground shadow-sm">
          <button
            type="button"
            onClick={() => onSave({ isContinue: true })}
            disabled={disabled}
            className={cn(
              "h-9 px-4 text-sm font-medium bg-transparent text-primary-foreground",
              "hover:bg-[hsl(var(--primary-hover))] hover:text-[hsl(var(--primary-hover-foreground))]",
              "active:bg-[hsl(var(--primary-active))] active:text-[hsl(var(--primary-active-foreground))]",
              "disabled:opacity-50",
            )}
          >
            Save
          </button>
          <button
            type="button"
            className={cn(
              "h-9 w-9 grid place-items-center bg-transparent text-primary-foreground border-l border-border/50",
              "hover:bg-[hsl(var(--primary-hover))] hover:text-[hsl(var(--primary-hover-foreground))]",
              "active:bg-[hsl(var(--primary-active))] active:text-[hsl(var(--primary-active-foreground))]",
              "disabled:opacity-50",
            )}
            aria-haspopup="menu"
            aria-expanded={open ? "true" : "false"}
            onClick={() => setOpen((v) => !v)}
            disabled={disabled}
            title="More save options"
          >
            <span className="sr-only">Toggle save menu</span>
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                open && "rotate-180",
              )}
              strokeWidth={2.5}
            />
          </button>
        </div>
        {open && (
          <div
            role="menu"
            className="absolute right-0 top-[calc(100%+6px)] min-w-[10rem] rounded-md bg-popover text-popover-foreground shadow-lg border border-border z-[300]"
          >
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-accent/80 hover:text-accent-foreground focus:outline-none cursor-pointer"
              onClick={() => {
                onSave({ isExit: true });
                setOpen(false);
              }}
            >
              <Save className="h-4 w-4" />
              <span className="truncate">Save & Close</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  // Row actions (kebab) for each profile
  const RowActions: React.FC<{ onDeleteClick: () => void }> = ({
    onDeleteClick,
  }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
      if (!open) return;
      const onDoc = (e: MouseEvent) => {
        const t = e.target as Node;
        if (ref.current && !ref.current.contains(t)) setOpen(false);
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onDoc);
        document.removeEventListener("keydown", onKey);
      };
    }, [open]);

    return (
      <div ref={ref} className="relative">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-lg bg-muted/40 ring-1 ring-border/60 hover:bg-muted/60"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          aria-label="Actions"
        >
          <MoreVertical className="h-4 w-4 text-foreground" />
        </Button>
        {open && (
          <div
            role="menu"
            className="absolute right-0 mt-2 w-40 rounded-md border border-border bg-popover text-popover-foreground shadow-md z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              role="menuitem"
              className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer"
              onClick={() => {
                onDeleteClick();
                setOpen(false);
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  };

  // Profiles list: server fetch + renderers (filtered by current group)
  const fetchRFProfiles = async ({
    page,
    pageSize,
    search,
    sort,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: { field: string; dir: "asc" | "desc" } | null;
  }): Promise<{ items: RFProfileItem[]; total: number }> => {
    const { accessToken } = (getTokensFromStorage() as any) || {};
    const term = (search || "").trim();

    // Ensure we have the group id (details may populate it after mount)
    const groupId =
      (formData as any)?.SRFProfileGroupId ??
      (tempState?.dataSet as any)?.SRFProfileGroupId ??
      (state?.dataSet as any)?.SRFProfileGroupId;

    if (!groupId) {
      return { items: [], total: 0 };
    }

    let serverFilters: any[] = [];
    if (term) {
      const base = [
        { ColumnName: "SRFProfilesName", Op: "contains", ColumnValue: term },
        { ColumnName: "SRFProfilesDetails", Op: "contains", ColumnValue: term },
        { ColumnName: "created_by", Op: "contains", ColumnValue: term },
        { ColumnName: "modified_by", Op: "contains", ColumnValue: term },
      ];
      const hasExisting =
        Array.isArray(serverFilters) && serverFilters.length > 0;
      const searchFilters = base.map((f, idx) =>
        idx === 0 && !hasExisting
          ? f
          : ({ LogicalOperator: "OR", ...f } as any),
      );
      serverFilters = Array.isArray(serverFilters)
        ? [...serverFilters, ...searchFilters]
        : searchFilters;
    }

    const doRequest = async (): Promise<{
      items: RFProfileItem[];
      total: number;
    }> => {
      const resp = await listRFProfile({
        accessToken,
        limit: pageSize,
        offset: page, // service treats as pageNumber (1-based)
        filter: serverFilters,
        sort: sort ? [{ field: sort.field, dir: sort.dir }] : undefined,
        moduleName: "RFProfile",
        isServiceWorker: false,
        listController: undefined,
        profileGroup: { SRFProfileGroupId: groupId },
      });
      const items = (resp as any)?.rfProfiles ?? [];
      const total = (resp as any)?.TotalCount ?? 0;
      return { items, total };
    };

    // Debounce only when the search term changes (typing). For pagination/sort (term unchanged), request immediately.
    if (term !== lastProfilesSearchTerm) {
      lastProfilesSearchTerm = term;
      if (profilesSearchDebounceTimer)
        clearTimeout(profilesSearchDebounceTimer);
      return new Promise<{ items: RFProfileItem[]; total: number }>(
        (resolve) => {
          profilesSearchDebounceTimer = setTimeout(async () => {
            const result = await doRequest();
            resolve(result);
          }, 350);
        },
      );
    }

    return await doRequest();
  };

  const renderRFProfileItem = (item: RFProfileItem) => {
    const active = !!(item as any)?.Active;
    const tenantName =
      (item as any)?.TenantName || (formData as any)?.TenantName;
    const createdBy = (item as any)?.created_by;
    const createdAt = (item as any)?.created_date;
    const updatedBy = (item as any)?.modified_by;
    const updatedAt = (item as any)?.modified_date;

    return (
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] items-start sm:items-stretch gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <span
                className={cn(
                  "inline-block h-2.5 w-2.5 rounded-full",
                  active ? "bg-emerald-500" : "bg-muted-foreground/40",
                )}
              />
              <div className="truncate text-lg font-semibold text-foreground">
                {item.SRFProfilesName}
              </div>
              {(item as any)?.tenant_name && (
                <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 px-2 py-0.5 text-xs font-medium">
                  {(item as any)?.tenant_name}
                </span>
              )}
            </div>
          </div>

          <div className="mt-2 text-base text-muted-foreground min-w-0">
            {!!item.SRFProfilesDetails &&
              (() => {
                const expanded = !!expandedDesc[item.SRFProfileId];
                return (
                  <div
                    className="whitespace-normal break-words md:max-w-[48rem] cursor-pointer select-none"
                    style={
                      expanded
                        ? undefined
                        : ({
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          } as any)
                    }
                    role="button"
                    tabIndex={0}
                    title={expanded ? "Click to collapse" : "Click to expand"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedDesc((prev) => ({
                        ...prev,
                        [item.SRFProfileId]: !expanded,
                      }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        setExpandedDesc((prev) => ({
                          ...prev,
                          [item.SRFProfileId]: !expanded,
                        }));
                      }
                    }}
                  >
                    {item.SRFProfilesDetails}
                  </div>
                );
              })()}
            {(item as any)?.AppURL && (
              <div className="mt-3 flex items-baseline gap-2 min-w-0">
                <span className="font-semibold whitespace-nowrap">
                  APP URL:
                </span>
                <span
                  className="truncate max-w-full sm:max-w-[60%]"
                  title={(item as any)?.AppURL}
                >
                  {(item as any)?.AppURL}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-row sm:flex-col justify-between items-center sm:items-end text-left sm:text-right gap-2 w-full sm:w-auto">
          <div className="order-2 sm:order-1 ml-auto sm:ml-0 shrink-0">
            <RowActions onDeleteClick={() => handleDeleteProfileOne(item)} />
          </div>
          <div className="order-1 sm:order-2 flex flex-col items-start sm:items-end text-left sm:text-right gap-1 max-w-full sm:max-w-[220px]">
            {(() => {
              const showUpdated = hasValue(updatedAt as any);
              const dateToShow = showUpdated
                ? (updatedAt as any)
                : (createdAt as any);
              const byToShow = showUpdated
                ? (updatedBy as any)
                : (createdBy as any);
              return (
                <>
                  <div className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDateShort(dateToShow as any)}
                  </div>
                  {hasValue(byToShow) && (
                    <div
                      className="text-xs text-muted-foreground/80 truncate"
                      title={String(byToShow)}
                    >
                      {String(byToShow)}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };

  const onProfileAdd = React.useCallback(() => {
    const tenantId = (formData as any)?.TenantID ?? (tenant as any)?.TenantId;
    const tenantName =
      (formData as any)?.TenantName ?? (tenant as any)?.TenantName;
    const profileGroupObj = {
      TenantId: tenantId,
      TenantName: tenantName,
      SRFProfileGroupId: currentGroupId,
      SRFProfileGroupName: (formData as any)?.SRFProfileGroupName,
    } as any;
    const profileGroupTemp = objectToBase64(profileGroupObj);
    navigate(`/smartrf/profile/new?profileGroup=${profileGroupTemp}`, {
      state: { profileGroup: profileGroupObj },
    });
  }, [navigate, currentGroupId, formData, tenant]);

  const onProfileItemClick = React.useCallback(
    (item: RFProfileItem) => {
      const tenantId =
        (item as any)?.TenantId ??
        (formData as any)?.TenantID ??
        (tenant as any)?.TenantId;
      const refId = objectToBase64({
        SRFProfileId: item?.SRFProfileId,
        TenantId: tenantId,
      });
      const tenantName =
        (formData as any)?.TenantName ?? (tenant as any)?.TenantName;
      const profileGroupObj = {
        TenantId: tenantId,
        TenantName: tenantName,
        SRFProfileGroupId: currentGroupId,
        SRFProfileGroupName: (formData as any)?.SRFProfileGroupName,
      } as any;
      const profileGroupTemp = objectToBase64(profileGroupObj);
      navigate(
        `/smartrf/profile/${tenantId}?refId=${refId}&profileGroup=${profileGroupTemp}`,
        {
          state: {
            edit: true,
            dataSet: item,
            profileGroup: profileGroupObj,
          },
        },
      );
    },
    [navigate, currentGroupId, formData, tenant],
  );

  // Delete selected profiles (used by ListView toolbar)
  const onDeleteProfiles = React.useCallback(
    async (selected: RFProfileItem[]) => {
      for (const it of selected) {
        try {
          await deleteRFProfile({
            data: {
              TenantId: (it as any)?.TenantId,
              SRFProfileId: it?.SRFProfileId,
            },
            moduleName: "RFProfile",
            isServiceWorker: false,
            listController: undefined,
          });
        } catch {
          // swallow per-item errors; ListView refreshes after handler completes
        }
      }
    },
    [],
  );

  const handleDeleteProfileOne = React.useCallback(
    (item: RFProfileItem) => {
      profileDeleteConfirmRef.current = async () => {
        await onDeleteProfiles([item]);
        setProfilesRefreshKey((k) => k + 1);
      };
      setProfileConfirmOpen(true);
    },
    [onDeleteProfiles],
  );

  const onConfirmDelete = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      const response: any = await deleteRFProfileGroup({
        data: formData,
        moduleName: "RFProfileGroup",
        isServiceWorker: false,
        listController: undefined,
      });
      if (response?.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: "Successfully Deleted",
            description: "Record has been deleted successfully.",
            style: "success",
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        navigate("/smartrf/profile-groups");
      } else {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response?.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      }
    } catch {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full px-3 sm:pl-4 sm:pr-0 py-4 lg:w-[calc(50vw+18rem-16px)] lg:max-w-full">
      {loading && (
        <div className="py-8 flex items-center justify-center">
          <Loader />
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Link
            to="/smartrf/profile-groups"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-muted/40 hover:bg-muted/60 text-foreground/90 ring-1 ring-border/60"
            aria-label="Back"
            title="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
                Profile Group
              </h1>
              <span className="text-muted-foreground">/</span>
              <p className="text-xl sm:text-2xl font-medium text-foreground truncate">
                {formData?.SRFProfileGroupName || "New"}
              </p>
            </div>
          </div>
        </div>

        {state?.edit && detailsDataModified && (
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-block rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs font-medium text-muted-foreground">
              Editing
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => setConfirmOpen(true)}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Details card */}
      <div className="mt-4 rounded-md border border-border bg-background shadow-[rgba(0,0,0,0.1)_0px_1px_4px]">
        <div className="p-4 space-y-4 lg:mr-[18rem]">
          <div className="grid grid-cols-1 gap-4">
            {/* Tenant */}
            <div
              className={cn(
                "space-y-1",
                isValidate(validateForm, formData?.TenantName) &&
                  "text-destructive",
              )}
            >
              <TenantDropdown
                textField="TenantName"
                label="Tenant"
                dataItemKey="TenantId"
                className="tenant-dropdown"
                tenant={tenant}
                setTenant={setTenant}
                defaultItem={{
                  TenantName: "Select Tenant ...",
                  disabled: true,
                }}
                editMode={state?.edit ? "ReadOnly" : ""}
                filterable={true}
                tenantFormId="TenantID"
                tenantFormName="TenantName"
                setFormData={setFormData}
                requiredField={true}
              />
              {isValidate(validateForm, formData?.TenantName) && (
                <p className="text-xs mt-1">This field cannot be empty</p>
              )}
            </div>

            {/* Name */}
            <div
              className={cn(
                isValidate(validateForm, formData?.SRFProfileGroupName) &&
                  "text-destructive",
              )}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <div className="flex items-center justify-between w-full sm:w-52 sm:pr-2">
                  <Label
                    htmlFor="SRFProfileGroupName"
                    className="text-sm font-medium text-muted-foreground"
                  >
                    Profile Group Name*
                  </Label>
                </div>
                <Input
                  id="SRFProfileGroupName"
                  name="SRFProfileGroupName"
                  value={formData?.SRFProfileGroupName || ""}
                  onChange={handleInputChange}
                  disabled={!detailsDataModified}
                  className="h-10 bg-background w-full flex-1"
                />
              </div>
              {isValidate(validateForm, formData?.SRFProfileGroupName) && (
                <p className="text-xs mt-1 sm:ml-52">
                  This field cannot be empty
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
              <div className="flex items-center justify-between w-full sm:w-52 sm:pr-2 sm:mt-2">
                <Label
                  htmlFor="SRFProfileGroupDetails"
                  className="text-sm font-medium text-muted-foreground"
                >
                  Description
                </Label>
              </div>
              <textarea
                id="SRFProfileGroupDetails"
                name="SRFProfileGroupDetails"
                value={formData?.SRFProfileGroupDetails || ""}
                onChange={handleInputChange}
                disabled={!detailsDataModified}
                className="h-24 w-full flex-1 resize-y rounded-md border border-border bg-background px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground shadow-sm focus:outline-none leading-[1.4]"
              />
            </div>
          </div>
          {(hasValue((formData as any)?.created_date) ||
            hasValue((formData as any)?.modified_date)) && (
            <div className="flex flex-col gap-1 text-sm">
              {hasValue((formData as any)?.created_date) && (
                <div className="min-w-0">
                  <span className="text-xs font-semibold text-muted-foreground mr-1">
                    Created
                  </span>
                  <span
                    className="text-foreground truncate"
                    title={`on ${formatDateShort((formData as any)?.created_date)} by ${String((formData as any)?.created_by || "-")}`}
                  >
                    on {formatDateShort((formData as any)?.created_date)} by{" "}
                    {(formData as any)?.created_by || "-"}
                  </span>
                </div>
              )}
              {hasValue((formData as any)?.modified_date) && (
                <div className="min-w-0">
                  <span className="text-xs font-semibold text-muted-foreground mr-1">
                    Updated
                  </span>
                  <span
                    className="text-foreground truncate"
                    title={`on ${formatDateShort((formData as any)?.modified_date)} by ${String((formData as any)?.modified_by || "-")}`}
                  >
                    on {formatDateShort((formData as any)?.modified_date)} by{" "}
                    {(formData as any)?.modified_by || "-"}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        {detailsDataModified && (
          <div className="px-4 py-3 flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              size="default"
              onClick={onExit}
              className="shrink-0"
            >
              Cancel
            </Button>
            <div className="shrink-0">
              <SplitSaveButton
                onSave={onSave}
                disabled={loading || !detailsDataModified}
              />
            </div>
          </div>
        )}
      </div>

      {/* Related Profiles (visible when group is available) */}
      {Boolean(currentGroupId) && (
        <>
          <div className="mt-[4rem] px-1 pb-2 text-sm font-semibold text-foreground">
            Profiles
          </div>
          <div className="mt-2 rounded-md border border-border bg-background shadow-[rgba(0,0,0,0.1)_0px_1px_4px]">
            <div className="p-3">
              <ListView<RFProfileItem>
                key={`${String(currentGroupId || "")}-${profilesRefreshKey}`}
                idKey="SRFProfileId"
                fetchList={fetchRFProfiles}
                itemRenderer={renderRFProfileItem}
                onItemClick={onProfileItemClick}
                onDelete={onDeleteProfiles}
                selectionMode="none"
                selectOnRowClick={false}
                showCheckboxes={false}
                hideSearch={true}
                extraToolbarRight={({ search, setSearch }) => (
                  <div className="flex items-center gap-2 w-full flex-nowrap">
                    <div className="flex-1 min-w-0">
                      <Input
                        placeholder="Search..."
                        value={search}
                        onChange={(e) =>
                          setSearch((e.target as HTMLInputElement).value)
                        }
                        className="h-9 w-full"
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={onProfileAdd}
                      className="shrink-0 whitespace-nowrap"
                    >
                      Add New
                    </Button>
                  </div>
                )}
                initialSort={{ field: "SRFProfileId", dir: "desc" }}
                pageSizeOptions={[10, 20, 50]}
                defaultPageSize={10}
              />
            </div>
          </div>
        </>
      )}

      {/* Delete profile confirmation */}
      {profileConfirmOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setProfileConfirmOpen(false)}
          />
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-profile-title"
            aria-describedby="confirm-profile-desc"
          >
            <div
              className="w-full max-w-sm rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b border-border">
                <h2
                  id="confirm-profile-title"
                  className="text-base font-semibold"
                >
                  Delete profile?
                </h2>
              </div>
              <div className="px-4 py-3">
                <p
                  id="confirm-profile-desc"
                  className="text-sm text-muted-foreground"
                >
                  This action cannot be undone. The record will be permanently
                  removed.
                </p>
              </div>
              <div className="px-4 py-3 flex justify-end gap-2 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setProfileConfirmOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    profileDeleteConfirmRef.current?.();
                    setProfileConfirmOpen(false);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete confirmation (shadcn-like dialog) */}
      {confirmOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setConfirmOpen(false)}
          />
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            aria-describedby="confirm-desc"
          >
            <div
              className="w-full max-w-sm rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b border-border">
                <h2 id="confirm-title" className="text-base font-semibold">
                  Delete profile group?
                </h2>
              </div>
              <div className="px-4 py-3">
                <p id="confirm-desc" className="text-sm text-muted-foreground">
                  This action cannot be undone. The record will be permanently
                  removed.
                </p>
              </div>
              <div className="px-4 py-3 flex justify-end gap-2 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={onConfirmDelete}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddRFProfileGroup;
